import { acceptHMRUpdate, defineStore } from 'pinia'
import missionService from '@/services/mission.service'
import specialModeService from '@/services/specialMode.service'
import { i18n } from '@/i18n'
import { useLocationStore } from '@/store/location'
import { clearStoredSpecialModeCode, getStoredSpecialModeCode, setStoredSpecialModeCode } from '@/utils/storage'

export const useMissionStore = defineStore({
  id: 'missionStore',
  state: () => ({
    missions: [],
    missionsFilter: {
      distance: '',
      point: '',
      categoryIds: []
    },
    missionsDetail: [],
    myMissions: [],
    myMissionIds: new Set(),
    selectedMissionId: '',
    selectedLocationId: '',
    specialModeCode: getStoredSpecialModeCode(),
    activeSpecialMode: null,
    specialModeError: null,
    isSpecialModeLoading: false,
    specialModePoints: 0
  }),
  setup () {},
  actions: {
    setMissionFavorite (missionId, locationId, isFavorite) {
      const sameMission = (entry) => String(entry?.mission?.id) === String(missionId)
      let updatedAtLocation = false

      ;(this.missions?.locations || []).forEach((item) => {
        if (String(item?.location?.id) !== String(locationId)) {
          return
        }
        ;(item.missions || []).filter(sameMission).forEach((entry) => {
          entry.isFavorite = isFavorite
          updatedAtLocation = true
        })
      })

      if (!updatedAtLocation) {
        ;(this.missions?.missionsWithoutLocation || []).filter(sameMission).forEach((entry) => {
          entry.isFavorite = isFavorite
        })
      }
    },
    resetFilter () {
      this.missionsFilter.distance = ''
      this.missionsFilter.point = ''
      this.missionsFilter.categoryIds = []
    },
    async getSelectedMissionId (missionId, locationId) {
      this.selectedMissionId = missionId
      this.selectedLocationId = locationId
      const locationStore = useLocationStore()

      try {
        const permissionState = await locationStore.refreshPermissionState()

        if (permissionState !== 'denied' && permissionState !== 'unsupported') {
          await locationStore.syncBrowserLocation({ persistToServer: true })
        }
      } catch (_) {
        // Konum güncellemesi başarısız olsa da görev detayını yüklemeye devam et.
      }

      return missionService.getSelectedMissionId(missionId, locationId).then(
        response => {
          this.missionsDetail = response.data
          return response
        }
      )
    },
    getMissions () {
      this.resetFilter()

      return missionService.getMissions(this.buildMissionPayload()).then(
        response => {
          this.hydrateMissionsWithMyMissions(response.data)
          this.missions = response.data
          this.syncSpecialModeFromResponse(response)
          return response
        }
      ).catch(error => {
        // Geçersiz mod kodunda, filtreyi temizleyip genel listeyi yeniden dene
        if (this.specialModeCode && error?.response?.status === 404) {
          this.specialModeError = error?.response?.data?.message || i18n.global.t('duties.eventCodeError')

          this.specialModeCode = ''
          clearStoredSpecialModeCode()

          return missionService.getMissions(this.buildMissionPayload()).then(
            response => {
              this.hydrateMissionsWithMyMissions(response.data)
              this.missions = response.data
              this.syncSpecialModeFromResponse(response)
              return response
            }
          )
        }

        throw error
      })
    },
    getMissionsMap () {
      this.resetFilter()

      return missionService.getMissions(this.buildMissionPayload({ map: true })).then(
        response => {
          this.missions = response.data
          this.syncSpecialModeFromResponse(response)
          return response
        }
      ).catch(error => {
        // Geçersiz mod kodunda, filtreyi temizleyip genel listeyi yeniden dene
        if (this.specialModeCode && error?.response?.status === 404) {
          this.specialModeError = error?.response?.data?.message || i18n.global.t('duties.eventCodeError')

          this.specialModeCode = ''
          clearStoredSpecialModeCode()

          return missionService.getMissions(this.buildMissionPayload({ map: true })).then(
            response => {
              this.missions = response.data
              this.syncSpecialModeFromResponse(response)
              return response
            }
          )
        }

        throw error
      })
    },
    getMissionsForFilter () {
      const filterData = { ...this.missionsFilter }
      filterData.categoryIds = filterData.categoryIds.join(',')

      return missionService.getMissions(this.buildMissionPayload(filterData)).then(
        response => {
          this.hydrateMissionsWithMyMissions(response.data)
          this.missions = response.data
          this.syncSpecialModeFromResponse(response)
          return response
        }
      ).catch(error => {
        // Geçersiz mod kodunda, filtreyi temizleyip genel listeyi yeniden dene
        if (this.specialModeCode && error?.response?.status === 404) {
          this.specialModeError = error?.response?.data?.message || i18n.global.t('duties.eventCodeError')

          this.specialModeCode = ''
          clearStoredSpecialModeCode()

          return missionService.getMissions(this.buildMissionPayload(filterData)).then(
            response => {
              this.hydrateMissionsWithMyMissions(response.data)
              this.missions = response.data
              this.syncSpecialModeFromResponse(response)
              return response
            }
          )
        }

        throw error
      })
    },
    getMyMissionList () {
      return missionService.getMyMissionList().then(
        response => {
          this.myMissions = response.data
          this.myMissionIds = new Set(
            this.myMissions
              .map(mission => mission?.mission?.id)
              .filter(Boolean)
              .map(id => String(id))
          )
          return response
        }
      )
    },
    isOnMyMission (missionId) {
      return this.myMissionIds.has(String(missionId))
    },
    getMyMissionItem (missionId) {
      return this.myMissions.find(mission => String(mission?.mission?.id) === String(missionId))
    },
    hydrateMissionsWithMyMissions (missionData) {
      const attachMyMission = (mission) => {
        const missionId = mission?.mission?.id
        if (!missionId || mission.myMission) {
          return
        }

        mission.myMission = this.getMyMissionItem(missionId) || null
      }

      ;(missionData?.locations || []).forEach((location) =>
        (location.missions || []).forEach(attachMyMission)
      )

      ;(missionData?.missionsWithoutLocation || []).forEach(attachMyMission)
    },
    getStartMission (missionId, locationId) {
      const formData = {
        id: missionId,
        locationId: locationId
      }
      return missionService.getStartMission(formData).then(
        response => {
          return response
        }
      )
    },
    buildMissionPayload (extra = {}) {
      const payload = { ...extra }
      if (this.specialModeCode && this.specialModeCode.trim() !== '') {
        payload.specialModeCode = this.specialModeCode.trim()
      }
      return payload
    },
    async applySpecialModeCode (code) {
      const trimmedCode = (code || '').trim()
      this.isSpecialModeLoading = true
      this.specialModeError = null

      try {
        let response
        if (trimmedCode === '') {
          response = await specialModeService.clear()
          this.specialModeCode = ''
          this.activeSpecialMode = null
          this.specialModePoints = 0
          clearStoredSpecialModeCode()
        } else {
          response = await specialModeService.select(trimmedCode)
          this.specialModeCode = trimmedCode
          this.activeSpecialMode = response.data?.specialMode || null
          this.specialModePoints = response.data?.specialModePoints || 0

          if (this.activeSpecialMode?.code) {
            setStoredSpecialModeCode(this.activeSpecialMode.code)
          }
        }
        return response
      } catch (error) {
        const message = error?.response?.data?.message || error.message || i18n.global.t('duties.eventCodeError')
        this.specialModeError = message

        this.activeSpecialMode = null
        this.specialModePoints = 0
        this.specialModeCode = ''
        clearStoredSpecialModeCode()

        throw error
      } finally {
        this.isSpecialModeLoading = false
      }
    },
    syncSpecialModeFromResponse (response) {
      const specialMode = response?.data?.activeSpecialMode || response?.data?.specialMode || null
      this.activeSpecialMode = specialMode
      this.specialModePoints = response?.data?.specialModePoints || 0

      if (specialMode && specialMode.code) {
        this.specialModeCode = specialMode.code
        setStoredSpecialModeCode(specialMode.code)
      } else if (!specialMode && (!this.specialModeCode || this.specialModeCode === '')) {
        clearStoredSpecialModeCode()
      }
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMissionStore, import.meta.hot))
}
